/**
 * Produto vendido em cada pedido. O pedido do Bling guarda os itens com descrição e SKU (`codigo`);
 * pedidos antigos podem ter só a descrição. Puro e sem I/O.
 */
import type { PlatformUnifiedOrder } from "../api/types.js";
import { isRevenueState } from "./state-machine.js";

export interface SoldItem {
  /** Chave estável do produto: SKU quando existe, senão o texto da descrição. */
  key: string;
  sku: string | null;
  name: string;
  quantity: number;
  unitPriceCents: number;
}

type LooseItem = { sku?: string | null; description?: string; productId?: string | null; quantity?: number; unitPriceCents?: number };

export function soldItems(order: Pick<PlatformUnifiedOrder, "items">): SoldItem[] {
  return (order.items as unknown as LooseItem[]).map((item) => {
    const sku = typeof item.sku === "string" && item.sku.trim() ? item.sku.trim() : null;
    const name = (typeof item.description === "string" && item.description.trim()) || sku || String(item.productId ?? "Item sem descrição");
    return { key: sku ?? name, sku, name, quantity: Number(item.quantity ?? 1), unitPriceCents: Number(item.unitPriceCents ?? 0) };
  });
}

/** "2× Laser 10W (SKU)" do primeiro item e "+N itens" quando há mais — o que a linha da lista mostra. */
export function productSummary(order: Pick<PlatformUnifiedOrder, "items">): { first: string; more: number } {
  const items = soldItems(order);
  if (items.length === 0) return { first: "Sem itens registrados", more: 0 };
  const [first] = items;
  return { first: `${first.quantity}× ${first.name}`, more: items.length - 1 };
}

/** Texto pesquisável dos itens (descrição + SKU), em minúsculas e sem acento. */
export function itemsSearchText(order: Pick<PlatformUnifiedOrder, "items">): string {
  return soldItems(order)
    .map((item) => `${item.name} ${item.sku ?? ""}`)
    .join(" ")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export interface TopProduct {
  key: string;
  sku: string | null;
  name: string;
  quantity: number;
  revenueCents: number;
  orders: number;
}

/** Produtos que mais venderam (por receita) entre os pedidos que valem venda. */
export function topProducts(orders: PlatformUnifiedOrder[], limit = 8): TopProduct[] {
  const byKey = new Map<string, TopProduct>();
  for (const order of orders) {
    if (!isRevenueState(order.state)) continue;
    const seen = new Set<string>();
    for (const item of soldItems(order)) {
      const row = byKey.get(item.key) ?? { key: item.key, sku: item.sku, name: item.name, quantity: 0, revenueCents: 0, orders: 0 };
      row.quantity += item.quantity;
      row.revenueCents += item.unitPriceCents * item.quantity;
      if (!seen.has(item.key)) { row.orders += 1; seen.add(item.key); }
      byKey.set(item.key, row);
    }
  }
  return [...byKey.values()].sort((a, b) => b.revenueCents - a.revenueCents || b.quantity - a.quantity).slice(0, limit);
}
